import {
  IconAlertHexagon,
  IconBuildingBank,
  IconClipboardList,
  IconCoin,
  IconUser,
} from '@u21/tabler-icons';
import {
  FormConfig,
  FormFieldSection,
  FormFields,
} from 'app/modules/filingFormGenerator/models';
import { GoamlReferenceTables } from 'app/modules/goAML/queries/useGetGoamlFiuConfig';
import { GoamlGeneratedFormValues } from 'app/modules/goAML/formConfig/models';
import { adaptLegacySchema } from 'app/modules/goAML/formConfig/schemaAdapter';
import {
  ACTION_FIELD,
  CURRENCY_CODE_LOCAL_FIELD,
  ENTITY_REFERENCE_FIELD,
  FIU_REFERENCE_FIELD,
  RENTITY_BRANCH_FIELD,
  RENTITY_ID_FIELD,
  REPORT_CODE_FIELD,
  REASON_FIELD,
  SUBMISSION_CODE_FIELD,
  SUBMISSION_DATE_FIELD,
} from 'app/modules/goAML/schemas/reporterData';
import { FILING_INFO_SCHEMA } from 'app/modules/goAML/schemas/filingInfo';
import { getReportingPersonFormFields } from 'app/modules/goAML/formConfig/reportingPersonFormConfig';
import {
  T_PERSON_TRANSACTION_MY_CLIENT_SCHEMA,
  T_PERSON_TRANSACTION_SCHEMA,
} from 'app/modules/goAML/schemas/tPersonTransactionSchema';
import { T_LOCATION_SCHEMA } from 'app/modules/goAML/schemas/tLocationSchema';
import { TRANSACTION_RAW_INPUTS } from 'app/modules/goAML/schemas/transactions';
import { T_FROM_RAW_INPUTS } from 'app/modules/goAML/schemas/fromSchema';
import { T_TO_RAW_INPUTS } from 'app/modules/goAML/schemas/toSchema';
import {
  T_ACCOUNT_MY_CLIENT_SCHEMA,
  T_ACCOUNT_SCHEMA,
} from 'app/modules/goAML/schemas/tAccountSchema';
import {
  T_ENTITY_MY_CLIENT_SCHEMA,
  T_ENTITY_SCHEMA,
} from 'app/modules/goAML/schemas/tEntitySchema';
import { FormFieldSchema } from 'app/shared/models/form';
import { U21FormFieldTypes } from 'app/shared/u21-ui/components';

export const GOAML_GENERATED_TABS = {
  FILING_INFO: 'filing-info',
  REPORTING_PERSON: 'reporting-person',
  LOCATION: 'location',
  TRANSACTIONS: 'transactions',
  INDICATORS: 'indicators',
} as const;

const SECTION_FLAGS = {
  mustExistInPayload: true,
  mustHaveValueIfExist: false,
} as const;

const fromTypeField: FormFieldSchema<string> = {
  name: 'from_type',
  label: 'Sender type',
  required: true,
  type: U21FormFieldTypes.SELECT,
  fieldProps: {
    options: [
      { value: 't_from.from_account', text: 'From account' },
      {
        value: 't_from_my_client.from_account',
        text: 'From account (my client)',
      },
      { value: 't_from.from_person', text: 'From person' },
      {
        value: 't_from_my_client.from_person',
        text: 'From person (my client)',
      },
      { value: 't_from.from_entity', text: 'From entity' },
      {
        value: 't_from_my_client.from_entity',
        text: 'From entity (my client)',
      },
    ],
  },
};

const toTypeField: FormFieldSchema<string> = {
  name: 'to_type',
  label: 'Receiver type',
  required: true,
  type: U21FormFieldTypes.SELECT,
  fieldProps: {
    options: [
      { value: 't_to.to_account', text: 'To account' },
      {
        value: 't_to_my_client.to_account',
        text: 'To account (my client)',
      },
      { value: 't_to.to_person', text: 'To person' },
      {
        value: 't_to_my_client.to_person',
        text: 'To person (my client)',
      },
      { value: 't_to.to_entity', text: 'To entity' },
      {
        value: 't_to_my_client.to_entity',
        text: 'To entity (my client)',
      },
    ],
  },
};

const indicatorField: FormFieldSchema<string> = {
  name: 'indicator',
  label: 'Indicator',
  type: U21FormFieldTypes.SELECT,
  required: true,
  fieldProps: { options: [] },
};

const PARTY_SECTIONS = [
  {
    key: 'from_account',
    label: 'From account',
    typeField: 'from_type',
    typeValue: 't_from.from_account',
    schema: T_ACCOUNT_SCHEMA,
  },
  {
    key: 'from_account_my_client',
    label: 'From account (my client)',
    typeField: 'from_type',
    typeValue: 't_from_my_client.from_account',
    schema: T_ACCOUNT_MY_CLIENT_SCHEMA,
  },
  {
    key: 'from_person',
    label: 'From person',
    typeField: 'from_type',
    typeValue: 't_from.from_person',
    schema: T_PERSON_TRANSACTION_SCHEMA,
  },
  {
    key: 'from_person_my_client',
    label: 'From person (my client)',
    typeField: 'from_type',
    typeValue: 't_from_my_client.from_person',
    schema: T_PERSON_TRANSACTION_MY_CLIENT_SCHEMA,
  },
  {
    key: 'from_entity',
    label: 'From entity',
    typeField: 'from_type',
    typeValue: 't_from.from_entity',
    schema: T_ENTITY_SCHEMA,
  },
  {
    key: 'from_entity_my_client',
    label: 'From entity (my client)',
    typeField: 'from_type',
    typeValue: 't_from_my_client.from_entity',
    schema: T_ENTITY_MY_CLIENT_SCHEMA,
  },
  {
    key: 'to_account',
    label: 'To account',
    typeField: 'to_type',
    typeValue: 't_to.to_account',
    schema: T_ACCOUNT_SCHEMA,
  },
  {
    key: 'to_account_my_client',
    label: 'To account (my client)',
    typeField: 'to_type',
    typeValue: 't_to_my_client.to_account',
    schema: T_ACCOUNT_MY_CLIENT_SCHEMA,
  },
  {
    key: 'to_person',
    label: 'To person',
    typeField: 'to_type',
    typeValue: 't_to.to_person',
    schema: T_PERSON_TRANSACTION_SCHEMA,
  },
  {
    key: 'to_person_my_client',
    label: 'To person (my client)',
    typeField: 'to_type',
    typeValue: 't_to_my_client.to_person',
    schema: T_PERSON_TRANSACTION_MY_CLIENT_SCHEMA,
  },
  {
    key: 'to_entity',
    label: 'To entity',
    typeField: 'to_type',
    typeValue: 't_to.to_entity',
    schema: T_ENTITY_SCHEMA,
  },
  {
    key: 'to_entity_my_client',
    label: 'To entity (my client)',
    typeField: 'to_type',
    typeValue: 't_to_my_client.to_entity',
    schema: T_ENTITY_MY_CLIENT_SCHEMA,
  },
] as const;

const buildPartySection = (
  party: (typeof PARTY_SECTIONS)[number],
  referenceTables: GoamlReferenceTables,
): FormFieldSection<Record<string, any>> => ({
  type: 'section',
  name: party.key,
  label: party.label,
  bindFieldsToParentPath: true,
  mustExistInPayload: false,
  mustHaveValueIfExist: false,
  dependentPaths: (sectionIndex) => [
    `transaction[${sectionIndex}].${party.typeField}`,
  ],
  hideIfTrue: {
    and: [
      {
        path: (sectionIndex) =>
          `transaction[${sectionIndex}].${party.typeField}`,
        conditions: (value) => value !== party.typeValue,
      },
    ],
  },
  fields: adaptLegacySchema(
    party.schema.map((field) => ({
      ...field,
      name: `${party.key}.${field.name}`,
    })),
    referenceTables,
  ),
});

const getTransactionFields = (
  referenceTables: GoamlReferenceTables,
): FormFields<Record<string, any>> => [
  ...adaptLegacySchema(
    [
      ...TRANSACTION_RAW_INPUTS,
      fromTypeField,
      ...T_FROM_RAW_INPUTS.map((field) => ({
        ...field,
        name: `from_details.${field.name}`,
      })),
    ],
    referenceTables,
  ),
  ...PARTY_SECTIONS.filter((party) => party.typeField === 'from_type').map(
    (party) => buildPartySection(party, referenceTables),
  ),
  ...adaptLegacySchema(
    [
      toTypeField,
      ...T_TO_RAW_INPUTS.map((field) => ({
        ...field,
        name: `to_details.${field.name}`,
      })),
    ],
    referenceTables,
  ),
  ...PARTY_SECTIONS.filter((party) => party.typeField === 'to_type').map(
    (party) => buildPartySection(party, referenceTables),
  ),
];

export const getGoamlGeneratedFormConfig = (
  referenceTables: GoamlReferenceTables,
): FormConfig<GoamlGeneratedFormValues>[] => [
  {
    tabLabel: 'Filing Info',
    tabKey: 'filing_info',
    tabIcon: <IconClipboardList />,
    tabPath: GOAML_GENERATED_TABS.FILING_INFO,
    tabSection: {
      type: 'tabSection',
      name: 'filing_info',
      label: 'Filing Info',
      fields: adaptLegacySchema(
        [
          ...FILING_INFO_SCHEMA,
          SUBMISSION_CODE_FIELD,
          REPORT_CODE_FIELD,
          FIU_REFERENCE_FIELD,
          SUBMISSION_DATE_FIELD,
          RENTITY_ID_FIELD,
          RENTITY_BRANCH_FIELD,
          ENTITY_REFERENCE_FIELD,
          CURRENCY_CODE_LOCAL_FIELD,
          REASON_FIELD,
          ACTION_FIELD,
        ],
        referenceTables,
      ),
      ...SECTION_FLAGS,
    },
  },
  {
    tabLabel: 'Reporting Person',
    tabKey: 'reporting_person',
    tabIcon: <IconUser />,
    tabPath: GOAML_GENERATED_TABS.REPORTING_PERSON,
    tabSection: {
      type: 'tabSection',
      name: 'reporting_person',
      label: 'Reporting Person',
      fields: getReportingPersonFormFields(referenceTables),
      ...SECTION_FLAGS,
    },
  },
  {
    tabLabel: 'Reporting Entity',
    tabKey: 'location',
    tabIcon: <IconBuildingBank />,
    tabPath: GOAML_GENERATED_TABS.LOCATION,
    tabSection: {
      type: 'tabSection',
      name: 'location',
      label: 'Reporting Entity Location',
      fields: adaptLegacySchema(T_LOCATION_SCHEMA, referenceTables),
      ...SECTION_FLAGS,
    },
  },
  {
    tabLabel: 'Transactions',
    tabKey: 'transaction',
    tabIcon: <IconCoin />,
    tabPath: GOAML_GENERATED_TABS.TRANSACTIONS,
    tabSection: {
      type: 'tabSection',
      name: 'transaction',
      label: 'Transaction',
      repeatable: true,
      repeatableButtonLabel: 'Transaction',
      defaultNumOfEntries: 0,
      fields: getTransactionFields(referenceTables),
      ...SECTION_FLAGS,
    },
  },
  {
    tabLabel: 'Indicators',
    tabKey: 'report_indicators',
    tabIcon: <IconAlertHexagon />,
    tabPath: GOAML_GENERATED_TABS.INDICATORS,
    tabSection: {
      type: 'tabSection',
      name: 'report_indicators',
      label: 'Report Indicator',
      repeatable: true,
      repeatableButtonLabel: 'Indicator',
      defaultNumOfEntries: 0,
      fields: adaptLegacySchema([indicatorField], referenceTables),
      ...SECTION_FLAGS,
    },
  },
];
